import React, { FC } from "react";
import { Button, Heading, Toggle } from "@telia/teddy";
import { connect, useDispatch } from "react-redux";
import { RootState } from "../../../../../../models";
import { FieldSet, FormRow, Input, FormRowFormats } from "../../../../forms";
import { VALIDATION_FIELD } from "../../../../rapidform/formValidation";
import {
    checkOrgName,
    checkOrgNumber,
    setShouldShowOrgOrNameInputField,
    setShouldShowInputFields,
    setShouldShowPBFields,
} from "../../../state/actions/businessActions";
import * as BUSINESS_DETAILS_SELECTORS from "../../../state/selectors/businessDetailsSelectors";
import TEXT from "../../../text-consts-b2b";
import OrgOrNameInput from "../../molecules/OrgOrNameInput/OrgOrNameInput";
import "./B2BHardwareOnly.less";

interface B2BHardwareOnlyProps {
    brregNameSearch: {
        name: string;
        orgNumber: string;
    }[];
    organizationName: string;
    shouldShowInputFields: boolean;
    shouldShowOrgOrNameInputField: boolean;
    shouldShowPBFields: boolean;
}

const B2BHardwareOnly: FC<B2BHardwareOnlyProps> = ({
    brregNameSearch,
    organizationName,
    shouldShowOrgOrNameInputField,
    shouldShowInputFields,
    shouldShowPBFields,
}) => {
    const dispatch = useDispatch();

    return (
        <FieldSet name="hardwareOnly">
            <Heading mb="200" as="h2" variant="title-200">
                {TEXT.PERSONAL_DETAILS.HARDWARE_ONLY.ORG_HEADING}
            </Heading>
            {shouldShowOrgOrNameInputField && (
                <OrgOrNameInput
                    checkOrgName={(name) => dispatch(checkOrgName(name))}
                    checkOrgNumber={(orgNumber) => dispatch(checkOrgNumber(orgNumber))}
                    businesses={brregNameSearch}
                    data-di-mask
                />
            )}
            {!shouldShowOrgOrNameInputField && (
                <div className="b2b-hardware-only__organization-row">
                    <div className="b2b-hardware-only__organization-name-container">
                        <span id="org-name">Organisasjonsnavn:</span>
                        <span data-di-mask>{organizationName}</span>
                    </div>
                    <>
                        <Button
                            type="button"
                            onClick={() => {
                                dispatch(setShouldShowOrgOrNameInputField(true));
                                dispatch(setShouldShowInputFields(false));
                            }}
                            variant="secondary"
                        >
                            Endre
                        </Button>
                    </>
                </div>
            )}
            {shouldShowInputFields && (
                <>
                    <div className="b2b-hardware-only__toggle-container">
                        <Toggle>
                            <Toggle.Input
                                checked={shouldShowPBFields}
                                onCheckedChange={() => dispatch(setShouldShowPBFields(!shouldShowPBFields))}
                            />
                            <Toggle.Label>{TEXT.PERSONAL_DETAILS.USE_POST_BOX}</Toggle.Label>
                        </Toggle>
                    </div>
                    {shouldShowPBFields && (
                        <>
                            <Input
                                type="text"
                                name={VALIDATION_FIELD.PB_NUMBER}
                                label={TEXT.PERSONAL_DETAILS.PB_NUMBER}
                                data-di-mask
                            />
                            <Input
                                type="text"
                                name={VALIDATION_FIELD.PB_NAME}
                                label={TEXT.PERSONAL_DETAILS.PB_NAME}
                                helpText={TEXT.PERSONAL_DETAILS.PB_NAME_HELPTEXT}
                                data-di-mask
                            />
                        </>
                    )}
                    {!shouldShowPBFields && (
                        <>
                            <Input
                                type="text"
                                name={VALIDATION_FIELD.STREET_CO}
                                label={TEXT.PERSONAL_DETAILS.STREET_CO}
                                helpText={TEXT.PERSONAL_DETAILS.STREET_CO_HELPTEXT}
                                data-di-mask
                            />
                            <Input
                                type="text"
                                name={VALIDATION_FIELD.STREET}
                                label={TEXT.PERSONAL_DETAILS.STREET}
                                data-di-mask
                            />
                        </>
                    )}
                    <FormRow format={FormRowFormats.FORTY_SIXTY} data-di-mask>
                        <Input type="text" name={VALIDATION_FIELD.POSTCODE} label={TEXT.PERSONAL_DETAILS.POSTCODE} />
                        <Input type="text" name={VALIDATION_FIELD.CITY} label={TEXT.PERSONAL_DETAILS.CITY} />
                    </FormRow>
                    <Heading mb="50" mt="200" as="h2" variant="title-200">
                        {TEXT.PERSONAL_DETAILS.HARDWARE_ONLY.CONTACT_PERSON_HEADING}
                    </Heading>
                    <Input
                        type="text"
                        name={VALIDATION_FIELD.FIRST_NAME}
                        label={TEXT.PERSONAL_DETAILS.FIRSTNAME}
                        data-di-mask
                    />
                    <Input
                        type="text"
                        name={VALIDATION_FIELD.LAST_NAME}
                        label={TEXT.PERSONAL_DETAILS.LASTNAME}
                        data-di-mask
                    />
                    <FormRow format={FormRowFormats.FORTY_SIXTY}>
                        <Input type="tel" name={VALIDATION_FIELD.PHONE} label={TEXT.COMMON.MOBILENUMBER} data-di-mask />
                        <Input
                            type="email"
                            name={VALIDATION_FIELD.EMAIL}
                            label={TEXT.PERSONAL_DETAILS.EMAIL}
                            placeholder="ola@nordmann.no"
                            data-di-mask
                        />
                    </FormRow>
                </>
            )}
        </FieldSet>
    );
};

const mapStateToProps = (state: RootState) => {
    return {
        // Business details
        brregNameSearch: BUSINESS_DETAILS_SELECTORS.getBrregNames(state),
        shouldShowOrgOrNameInputField: BUSINESS_DETAILS_SELECTORS.shouldShowOrgOrNameInputField(state),
        shouldShowInputFields: BUSINESS_DETAILS_SELECTORS.shouldShowInputFields(state),
        shouldShowPBFields: BUSINESS_DETAILS_SELECTORS.shouldShowPBFields(state),
        organizationName: BUSINESS_DETAILS_SELECTORS.getOrganizationName(state),
    };
};

export default connect(mapStateToProps)(B2BHardwareOnly);
